import {
  type GeneratedNodeAttribute,
  type InstanceContact,
  isContactAttributeType,
  isFillableFromContactType,
  parseContactAttributeValue,
} from "@keydials/api-client";

/**
 * A Contact attribute (KEY-752) names one entry in the dial's own contacts list — the dentist, the school
 * office — saved once with a phone, address and email. Like `personAttribute.ts`, everything the field needs
 * is on the instance detail (`contacts` comes with every fetch), and a value naming a contact since removed
 * from the list is kept and flagged, never blanked.
 */

export interface ContactAttributeState {
  /** The id the value names; null when unset. */
  id: string | null;
  /** The list entry, or null when unset or when the id names nobody on the list any more. */
  contact: InstanceContact | null;
  /** The value names somebody no longer on the list: kept as it was and flagged. */
  unresolved: boolean;
  /** The contacts the picker offers, in the dial's own order. */
  options: InstanceContact[];
}

/** Resolves what a Contact field should show and offer, against the contacts already on the instance detail. */
export function contactAttributeState(value: string, contacts: InstanceContact[] | null | undefined): ContactAttributeState {
  const list = contacts ?? [];
  const id = parseContactAttributeValue(value);
  // Ids arrive canonicalised, but a value could predate that or arrive by import: compare case-insensitively.
  const contact = id ? list.find((c) => c.id.toLowerCase() === id.toLowerCase()) ?? null : null;
  return { id, contact, unresolved: id !== null && contact === null, options: [...list].sort((a, b) => a.order - b.order) };
}

/** The contact a stored value names, or null. */
export function resolveContact(value: string | null | undefined, contacts: InstanceContact[] | null | undefined): InstanceContact | null {
  return contactAttributeState(value ?? "", contacts).contact;
}

/**
 * A `tel:` link for a phone number as typed. The number is stored exactly as somebody wrote it ("01632 960 123
 * ext 4"), so the link keeps only what a dialler understands — digits, a leading +, and `*`/`#` — and stops at
 * an extension rather than dialling it into the main number, and drops a "(0)" written after a country code. Null when nothing diallable is left.
 */
export function telHref(phone: string | null | undefined): string | null {
  const raw = (phone ?? "").trim();
  if (!raw) return null;
  const main = raw.split(/\s*(?:ext\.?|extension|x)\s*\d/i)[0];
  const plus = main.trim().startsWith("+") ? "+" : "";
  // "+44 (0)1632…" — the bracketed trunk zero is for dialling at home, and wrong after a country code.
  const digits = (plus ? main.replace(/\(0\)/g, "") : main).replace(/[^\d*#]/g, "");
  return digits.length > 0 ? `tel:${plus}${digits}` : null;
}

/** A `mailto:` link for an email address, or null when there is none. */
export function mailtoHref(email: string | null | undefined): string | null {
  const trimmed = (email ?? "").trim();
  return trimmed ? `mailto:${trimmed}` : null;
}

const escapeHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * A contact's address shaped for the field it pre-fills: one line for a Text box (which cannot hold a line
 * break), paragraph-and-breaks for a RichText one. Blank lines are dropped either way.
 */
export function addressForField(address: string, targetType: string): string {
  const lines = address.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
  if (lines.length === 0) return "";
  return targetType === "RichText" ? `<p>${lines.map(escapeHtml).join("<br>")}</p>` : lines.join(", ");
}

/**
 * What `target` should be pre-filled with from a contact (KEY-752) — "With: Smile Dental" fills in "Where" —
 * or null for nothing. A pre-fill is a DISPLAY suggestion, exactly like a declared default (KEY-286): the
 * field shows it unsaved with Save and Cancel, nothing is stored until the person saves, so a status gate
 * never sees it. It is offered only while the target's STORED value is empty — a Where somebody already
 * wrote is never overwritten — and only from a Contact field on the same node (`decls` is that node's
 * declarations, `valueOf` its stored values) whose chosen contact has an address.
 */
export function contactPrefillFor(
  target: GeneratedNodeAttribute,
  decls: readonly GeneratedNodeAttribute[],
  valueOf: (key: string) => string,
  contacts: InstanceContact[] | null | undefined,
): string | null {
  if (!isFillableFromContactType(target.type) || valueOf(target.key).trim().length > 0) return null;
  for (const source of decls) {
    if (!isContactAttributeType(source.type) || source.fillsAttributeKey !== target.key || source.target !== target.target) continue;
    const address = resolveContact(valueOf(source.key), contacts)?.address;
    if (address && address.trim()) {
      const shaped = addressForField(address, target.type);
      if (shaped) return shaped;
    }
  }
  return null;
}
